/**
 * @author EdenCha <eden@nurigo.net>
 */
import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import withStyles from '@material-ui/core/styles/withStyles'
import AppBar from '@material-ui/core/AppBar'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import Hidden from '@material-ui/core/Hidden'
import BottomNavigation from '@material-ui/core/BottomNavigation'
import BottomNavigationAction from '@material-ui/core/BottomNavigationAction'
import Grid from '@material-ui/core/Grid'
import LinearProgress from '@material-ui/core/LinearProgress'
import Collapse from '@material-ui/core/Collapse'
import AddIcon from '@material-ui/icons/Add'
import HomeIcon from '@material-ui/icons/Home'
import HelpIcon from '@material-ui/icons/Help'
import AppstoreSearchBar from 'components/molecules/AppstoreSearchBar'
import LogoImg from 'static/image/logo.png'
import CoolsmsLogoImg from 'static/image/coolsms.png'
import AppstoreLogoImg from 'static/image/app_logo.png'
import config from 'config'

const styles = theme => ({
  root: {
    flexGrow: 1,
    zIndex: 1,
    overflow: 'hidden',
    position: 'relative',
    display: 'flex'
  },
  appBar: {
    backgroundColor: '#fff',
    boxShadow: '0px 4px 8px -3px rgba(17, 17, 17, .06)'
  },
  appBottomBar: {
    top: 'auto',
    bottom: 0,
    backgroundColor: theme.palette.background.default
  },
  navIconHide: {
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  toolbarArea: theme.mixins.toolbar,
  toolbar: {
    justifyContent: 'space-between'
  },
  headerMenu: {
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  content: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2)
    },
    minHeight: '100vh',
    boxSizing: 'border-box'
  },
  logoImg: {
    width: 100
  },
  appLogoImg: {
    width: 130
  },
  headerTitle: {
    flex: 1,
    position: 'relative',
    [theme.breakpoints.up('sm')]: {
      top: 5
    },
    paddingLeft: 10,
    color: '#666',
    fontSize: '1.2em',
    fontWeight: 300,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  addAppBtn: {
    marginRight: '1em'
  },
  createAppLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none'
  },
  bottomNavLabel: {
    marginTop: '0.2em'
  },
  progressArea: {
    position: 'absolute',
    top: 0,
    width: '100%'
  }
})

const AppstoreLayout = props => {
  const { classes, children, ...others } = props
  const { appListLoading, title } = others
  return (
    <div className={classes.root}>
      <AppBar className={classes.appBar}>
        <Collapse in={appListLoading} className={classes.progressArea}>
          <div>
            <LinearProgress />
          </div>
        </Collapse>
        <Toolbar className={classes.toolbar}>
          <Hidden smDown>
            <div>
              <Grid container spacing={1} alignItems="center" justify="center">
                <Grid item>
                  <Link to="/dashboard">
                    <img
                      className={classes.logoImg}
                      src={config.isCoolsms ? CoolsmsLogoImg : LogoImg}
                      alt="logo"
                      title="logo"
                    />
                  </Link>
                </Grid>
                <Grid item>
                  <a href={config.pages.appList}>
                    <img
                      className={classes.appLogoImg}
                      src={AppstoreLogoImg}
                      alt="app_logo"
                      title="app_logo"
                    />
                  </a>
                </Grid>
                <Grid item>
                  <Typography
                    variant="title"
                    color="default"
                    className={classes.headerTitle}
                  >
                    {title}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          </Hidden>
          <div className={classes.headerMenu}>
            <AppstoreSearchBar {...others} />
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.toolbarArea} />
        {children}
        <Hidden mdUp>
          <div className={classes.toolbarArea} />
        </Hidden>
        <Hidden smDown>
          <Grid container spacing={1} justify="center">
            <Grid item xs={12}>
              <Typography align="center" variant="body2">
                앱을 등록 후 수익을 창출하세요!{' '}
                <Link to="/me/apps" className={classes.createAppLink}>
                  [앱 등록하기]
                </Link>
              </Typography>
            </Grid>
            <Grid item>
              <Typography align="center" variant="body2">
                <a
                  href={
                    config.isCoolsms
                      ? 'https://support.coolsms.co.kr/hc/ko/articles/360046068712'
                      : 'https://support.solapi.com/hc/ko/articles/360021221053'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.createAppLink}
                >
                  앱 스토어 도움말
                </a>
              </Typography>
            </Grid>
            <Grid item>
              <Typography align="center" variant="body2">
                <a href={config.homePath} className={classes.createAppLink}>
                  {config.serviceName} 홈
                </a>
              </Typography>
            </Grid>
          </Grid>
        </Hidden>
      </main>
      <Hidden mdUp>
        <AppBar className={classes.appBottomBar}>
          <BottomNavigation showLabels>
            <BottomNavigationAction
              label="앱 등록"
              onClick={e => {
                window.location.href = '/me/apps'
              }}
              icon={<AddIcon />}
              classes={{
                label: classes.bottomNavLabel
              }}
            />
            <BottomNavigationAction
              label={config.serviceName + ' 홈'}
              onClick={e => {
                window.location.href = config.homePath
              }}
              icon={<HomeIcon />}
              classes={{
                label: classes.bottomNavLabel
              }}
            />
            <BottomNavigationAction
              label="도움말"
              onClick={e => {
                window.location.href = config.isCoolsms
                  ? 'https://support.coolsms.co.kr/hc/ko/articles/360046068712'
                  : 'https://support.solapi.com/hc/ko/articles/360021221053'
              }}
              icon={<HelpIcon />}
              classes={{
                label: classes.bottomNavLabel
              }}
            />
          </BottomNavigation>
        </AppBar>
      </Hidden>
    </div>
  )
}

AppstoreLayout.propTypes = {
  classes: PropTypes.object.isRequired,
  children: PropTypes.object.isRequired
}

export default withStyles(styles, { withTheme: true })(AppstoreLayout)
